import type { BladeField, ViewDefinition } from '../BladeDataSource';

// Minimal HUB-277 binding-renderer resolve for the record primitive (HUB-408).
// This is the FIRST real consumption of a server-side ViewDefinition: it maps a
// definition's sections/fields onto the entity's already-fetched fields
// (matched by canonical key), so the record layout — which fields show, in what
// order, under which headings, with which labels/render hints — is data-driven
// from the dictionary rather than hardcoded in TSX.
//
// Scope: this resolves a flat record from fields the source already projected.
// The fuller renderer (dot-path projection into raw entities, masking-policy
// injection via `can`, all primitives) is HUB-277 — see its design doc. PHI
// note: values here already passed server-side redaction (HUB-357); a
// restricted field keeps its `restricted` flag so the primitive still renders
// the placeholder.

export interface ResolvedRecordSection {
  id: string;
  label?: string;
  fields: BladeField[];
}

/** Map a ViewDefinition onto pre-fetched entity fields. Fields the definition
 *  omits are dropped; fields it lists but the entity didn't supply are skipped
 *  (no empty labeled rows — absence must not leak field existence). Returns []
 *  when nothing resolves, letting the caller fall back to the hardcoded layout. */
export function resolveRecordSections(
  def: ViewDefinition,
  fields: BladeField[],
): ResolvedRecordSection[] {
  const byKey = new Map<string, BladeField>();
  for (const f of fields) {
    if (f.key) byKey.set(f.key, f);
  }

  const sections: ResolvedRecordSection[] = [];
  for (const section of def.sections) {
    const resolved: BladeField[] = [];
    for (const vf of section.fields) {
      const src = byKey.get(vf.field);
      if (!src) continue;
      resolved.push({
        ...src,
        label: vf.label || src.label,
        mono: renderMono(vf.as) ?? src.mono,
      });
    }
    if (resolved.length > 0) {
      sections.push({ id: section.id, label: section.label, fields: resolved });
    }
  }
  return sections;
}

/** Translate the `as` render-hint idiom to the record primitive's mono flag.
 *  Unknown/absent hints return undefined (inherit the source field's flag). */
function renderMono(as: string | undefined): boolean | undefined {
  if (as === 'mono' || as === 'code' || as === 'id') return true;
  return undefined;
}
